export type MetricType = 'currency' | 'percentage' | 'count' | 'number' | 'text';

export interface SummaryMetricConfig {
  apiField: string;
  label: string;
  metric: MetricType;
}

export interface InitiativeConfig {
  metrics: SummaryMetricConfig[];
  summaryMetricOrder?: string[];
  showViewDetails?: boolean;
  trend?: {
    previousApiField: string;
    currentApiField: string;
    label: string;
  };
}

export const SUMMARY_METRIC_CONFIG: Record<string, InitiativeConfig> = {
  'Evaluation & Management Sloping': {
    metrics: [
      {
        apiField: 'PotentialSavingsMarketBenchmarkFullyInsured',
        label: 'Potential Savings Market Benchmark Fully Insured',
        metric: 'currency',
      },
      { apiField: 'YoY2024', label: '2024 Year Over Year View', metric: 'currency' },
      { apiField: 'YoY2025', label: '2025 Year Over Year View', metric: 'currency' },
    ],
  },
  'Medicare - Midlevel Remediation (Recontracting)': {
    metrics: [
      {
        apiField: 'reason',
        label: 'Reason',
        metric: 'text',
      },
      { apiField: 'date_excluded', label: 'Date Excluded', metric: 'text' },
      { apiField: 'NdbPaperType', label: 'NDB Paper Type', metric: 'text' },
    ],
  },
  'Progressive Schedules': {
    metrics: [
      {
        apiField: 'mkt_schdl_name',
        label: 'Market Schedule Name',
        metric: 'text',
      },
      { apiField: 'spend_2024', label: '2024 Spend', metric: 'text' },
      { apiField: 'spend_2025', label: '2025 Spend', metric: 'text' },
    ],
  },
  'Medicare Remediation': {
    summaryMetricOrder: ['MedicareAllowedAmount', 'AllowedAmount'],
    metrics: [
      { apiField: 'MedicareAllowedAmount', label: 'Total Medicare Allowed', metric: 'currency' },
      { apiField: 'AllowedAmount', label: 'Total Allowed', metric: 'currency' },
    ],
  },
  'Egregious Physicians': {
    metrics: [{ apiField: 'AllowedAmount', label: 'Total Allowed Amount', metric: 'currency' }],
  },
  'Virtual Care Greater Than 95% to Case Rates': {
    metrics: [
      { apiField: 'AllowedAmount2024', label: 'Total Virtual Allowed 2024', metric: 'currency' },
      { apiField: 'AllowedAmount2025', label: 'Total Virtual Allowed 2025', metric: 'currency' },
    ],
    trend: {
      previousApiField: 'allowed_amt_2024_raw',
      currentApiField: 'allowed_amt_2025_raw',
      label: 'Year Over Year',
    },
  },
  'Hard Codes': {
    metrics: [
      { apiField: 'InnRateAverage', label: 'In-Network Rate Average', metric: 'currency' },
      { apiField: 'InnRateMax', label: 'In-Network Rate Maximum', metric: 'currency' },
    ],
  },
  Radiology: {
    metrics: [
      { apiField: 'DollarChange', label: 'Remediation Opportunity', metric: 'currency' },
      {
        apiField: 'FiDollarChange',
        label: 'Fully Insured Remediation Opportunity',
        metric: 'currency',
      },
    ],
  },
  'Hearing Aids': {
    metrics: [
      { apiField: 'Units', label: 'Unit Count', metric: 'count' },
      { apiField: 'AllowedAmount', label: 'Allowed Amount', metric: 'currency' },
      { apiField: 'RepricedAllowedAmount', label: 'Repriced Allowed Amount', metric: 'currency' },
      { apiField: 'DollarChange', label: 'Remediation Opportunity', metric: 'currency' },
    ],
  },
  'Consult Before Colonoscopy': {
    metrics: [
      { apiField: 'Units', label: 'Unit Count', metric: 'count' },
      { apiField: 'AllowedAmount', label: 'Allowed Amount', metric: 'currency' },
      { apiField: 'RepricedAllowedAmount', label: 'Repriced Allowed Amount', metric: 'currency' },
      { apiField: 'DollarChange', label: 'Remediation Opportunity', metric: 'currency' },
    ],
  },
  'Oral Appliance Remediation': {
    metrics: [
      { apiField: 'Units', label: 'Unit Count', metric: 'count' },
      { apiField: 'AllowedAmount', label: 'Allowed Amount', metric: 'currency' },
      { apiField: 'RepricedAllowedAmount', label: 'Repriced Allowed Amount', metric: 'currency' },
      { apiField: 'DollarChange', label: 'Remediation Opportunity', metric: 'currency' },
    ],
  },
  'S Code Remediation': {
    metrics: [
      { apiField: 'Units', label: 'Unit Count', metric: 'count' },
      { apiField: 'AllowedAmount', label: 'Allowed Amount', metric: 'currency' },
      { apiField: 'RepricedAllowedAmount', label: 'Repriced Allowed Amount', metric: 'currency' },
      { apiField: 'DollarChange', label: 'Remediation Opportunity', metric: 'currency' },
    ],
  },
  'Urgent Care or Convenience Care Clinics': {
    metrics: [
      { apiField: 'NdbPaperType', label: 'NDM Paper Type', metric: 'text' },
      { apiField: 'PayAtUrgentCareRate', label: 'Pay at UH Rate', metric: 'text' },
    ],
  },
  'Stagnant Schedules': {
    metrics: [{ apiField: 'ClaimCount', label: 'Claim Count', metric: 'count' }],
  },
  'PPR Physicians': {
    metrics: [{ apiField: 'PprPercentage', label: 'PPR Percentage', metric: 'percentage' }],
  },
  'Non-Standard Fee Schedules - COSMOS': { metrics: [] },
  'Non-Standard Fee Schedules - CSP': { metrics: [] },
  'Non-Standard Fee Schedules - CIRRUS': { metrics: [] },
  'Non-Standard Fee Schedules - NDB': { metrics: [] },
};

const API_FIELD_ALIASES: Record<string, string> = {
  PotentialSavingsMarketBenchmarkFi: 'PotentialSavingsMarketBenchmarkFullyInsured',
};

export const getConfiguredMetric = (
  initiative: string,
  apiField: string
): SummaryMetricConfig | undefined => {
  const configuredField = API_FIELD_ALIASES[apiField] ?? apiField;
  return SUMMARY_METRIC_CONFIG[initiative]?.metrics.find(
    (metric) => metric.apiField === configuredField
  );
};

export const getConfiguredMetrics = (initiative: string): SummaryMetricConfig[] =>
  SUMMARY_METRIC_CONFIG[initiative]?.metrics ?? [];

export const getSummaryMetricOrder = (initiative: string): string[] =>
  SUMMARY_METRIC_CONFIG[initiative]?.summaryMetricOrder ?? [];

export const getConfiguredMetricLabel = (
  initiative: string,
  apiField: string
): string | undefined => getConfiguredMetric(initiative, apiField)?.label;

export const getConfiguredTrend = (initiative: string) => SUMMARY_METRIC_CONFIG[initiative]?.trend;
